import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'

import NavigationArrow from 'components/NavigationArrow/NavigationArrow'

import styles from './KineticScrolling.scss'

const TIME_CONSTANT = 325

class KineticScrolling extends React.Component {
    static propTypes = {
        children: PropTypes.arrayOf(PropTypes.node).isRequired,
        snap: PropTypes.number,
        trackState: PropTypes.func,
    }

    constructor(props) {
        super(props)

        this.state = {
            pressed: false,
            reference: null,
            offset: 0,
            min: 0,
            max: 0,
            dragging: false,
        }

        this.prev = this.prev.bind(this)
        this.next = this.next.bind(this)

        this.handleTap = this.handleTap.bind(this)
        this.handleDrag = this.handleDrag.bind(this)
        this.handleRelease = this.handleRelease.bind(this)
        this.handleClick = this.handleClick.bind(this)
        this.handleWheel = this.handleWheel.bind(this)
        this.handleResize = this.handleResize.bind(this)

        this.autoScroll = this.autoScroll.bind(this)
        this.scrollToItem = this.scrollToItem.bind(this)
        this.update = this.update.bind(this)
    }

    componentDidMount() {
        this.update()

        window.addEventListener('resize', this.handleResize)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize)
    }

    componentDidUpdate(prevProps) {
        if (this.props.children.length !== prevProps.children.length) {
            this.update()
        }
    }

    update() {
        const max = this._view.offsetWidth - this._holder.offsetWidth
        this.setState({ max }, () => this.scroll(this.state.offset))
    }

    pos(e) {
        // touch event
        const clientPos = 'clientX'
        if (e.targetTouches && (e.targetTouches.length >= 1)) {
            return e.targetTouches[0][clientPos]
        }

        // mouse event
        return e[clientPos]
    }

    scroll(x) {
        const offset = Math.min(Math.max(x, this.state.min), this.state.max) // clamp

        this.setState({ offset })
        this.trackState()
    }

    track() {
        const now = Date.now()
        const elapsed = now - this.state.timestamp
        this.setState({
            timestamp: now,
        })
        const delta = this.state.offset - this.state.frame
        const v = 1000 * delta / (1 + elapsed)

        this.setState({
            frame: this.state.offset,
            velocity: 0.8 * v + 0.2 * this.state.velocity,
        })
    }

    autoScroll() {
        let elapsed
        let delta

        if (this.state.amplitude) {
            elapsed = Date.now() - this.state.timestamp
            delta = this.state.amplitude * Math.exp(-elapsed / TIME_CONSTANT)

            if (delta > 5 || delta < -5) {
                this.scroll(this.state.target - delta)
                requestAnimationFrame(this.autoScroll)
            } else {
                this.scroll(this.state.target)
            }
        }
    }

    scrollToItem(idx) {
        if (this.props.snap) {
            const target = idx * this.props.snap
            const amplitude = target - this.state.offset

            this.setState({
                target,
                amplitude,
                timestamp: Date.now(),
                enableTransitions: true,
            })

            this.scroll(target)
        }
    }

    prev() {
        if (this.props.snap) {
            this.scrollToItem(this.current() - 1)
        }
    }

    next() {
        if (this.props.snap) {
            this.scrollToItem(this.current() + 1)
        }
    }

    current() {
        let current
        if (this.state.offset === 0) {
            current = 0
        } else {
            current = Math.round(this.state.offset / this.props.snap)
        }
        return current
    }

    atBegin() {
        return this.state.offset === 0
    }

    atEnd() {
        return this.state.offset === this.state.max
    }

    handleTap(e) {
        this.setState({
            pressed: true,
            reference: this.pos(e),
            velocity: 0,
            amplitude: 0,
            timestamp: Date.now(),
            frame: this.state.offset,
        })

        clearInterval(this.ticker)
        const boundTrack = this.track.bind(this)
        this.ticker = setInterval(boundTrack, 100)

        this.trackState()

        window.addEventListener('mousemove', this.handleDrag)
        window.addEventListener('mouseup', this.handleRelease)

        e.preventDefault()
        e.stopPropagation()
        return false
    }

    handleDrag(e) {
        let x
        let delta

        if (this.state.pressed) {
            x = this.pos(e)
            delta = this.state.reference - x

            if (delta > 2 || delta < -2) {
                this.setState({
                    reference: x,
                    dragging: true,
                    enableTransitions: false,
                })

                this.scroll(this.state.offset + delta)
            }
        }

        e.preventDefault()
        e.stopPropagation()
        return false
    }

    handleRelease(e) {
        window.removeEventListener('mousemove', this.handleDrag)
        window.removeEventListener('mouseup', this.handleRelease)

        this.setState({
            pressed: false,
        })

        clearInterval(this.ticker)

        if (!this.state.dragging) return

        let amplitude
        let target = this.state.offset

        if (this.state.velocity > 10 || this.state.velocity < -10) {
            amplitude = 0.2 * this.state.velocity
            target = Math.round(this.state.offset + amplitude)
        }

        target = Math.round(target / this.props.snap) * this.props.snap
        amplitude = target - this.state.offset

        this.setState({
            target,
            amplitude,
            timestamp: Date.now(),
        })

        this.autoScroll()
        if (!e.currentTarget.getAttribute || (e.currentTarget.getAttribute('id') !== this._view.id)) {
            this.setState({
                dragging: false,
            })
        }
    }

    handleWheel(e) {

        if (this.props.snap) {
            if (e.deltaY > 0) {
                this.next()
            } else {
                this.prev()
            }
            return
        }

        const target = this.state.offset + e.deltaY

        const amplitude = target - this.state.offset

        this.setState({
            target,
            amplitude,
            timestamp: Date.now(),
            enableTransitions: false,
        })

        this.autoScroll()
    }

    handleClick() {
        this.setState({
            dragging: false,
        })
    }

    handleResize() {
        this.update()
    }

    trackState() {
        if (this.props.trackState) {
            this.props.trackState({
                dragging: this.state.dragging,
            })
        }
    }

    render() {
        const transform = `translateX(-${this.state.offset}px)`

        const wrapperClassName = classNames(styles.wrapper, {
            [styles.scrolledLeft]: !this.atBegin(),
            [styles.scrolledRight]: !this.atEnd(),
        })

        return (
            <div className={styles.base}>
                <div className={styles.arrow}>
                    <NavigationArrow
                        type='left'
                        handleClick={this.prev}
                        disabled={this.atBegin()}
                    />
                </div>
                <div className={wrapperClassName} ref={_ => this._holder = _}>
                    <div
                        ref={_ => this._view = _}
                        style={{ transform }}
                        className={styles.view}
                        onMouseDown={this.handleTap}
                        onMouseUp={this.handleRelease}
                        onClick={this.handleClick}
                        onWheel={this.handleWheel}
                    >
                        {this.props.children}
                    </div>
                </div>
                <div className={styles.arrow}>
                    <NavigationArrow
                        type='right'
                        handleClick={this.next}
                        disabled={this.atEnd()}
                    />
                </div>
            </div>
        )
    }
}

export default KineticScrolling



// WEBPACK FOOTER //
// ./components/KineticScrolling/KineticScrolling.jsx